import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { useApp } from "../../contexts/AppContext";
import { t } from "../../utils/localization";
import styles from "./Slider.module.css";

export default function Slider() {
  const { state } = useApp();
  const [currentSlide, setCurrentSlide] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const videoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});
  const [videoKeys, setVideoKeys] = useState<{ [key: number]: string }>({});

  const {
    data: slides,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["slider", { active: true }],
    queryFn: () => apiService.getSliders({ active: true }),
    staleTime: 300000, // 5 minutes
  });

  // Generate stable video keys when slides change
  useEffect(() => {
    if (slides) {
      const newKeys: { [key: number]: string } = {};
      slides.forEach((slide, index) => {
        if (slide.type === "VIDEO") {
          newKeys[index] = `${slide.id}-${slide.url}`;
        }
      });
      setVideoKeys(newKeys);
    }
  }, [slides]);

  // Stable video end handler
  const handleVideoEnd = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides!.length);
  }, [slides]);

  // Handle video playback when slide becomes active
  useEffect(() => {
    if (!slides || slides.length === 0) return;

    // Pause all videos if screensaver is active
    if (state.isScreensaverActive) {
      Object.values(videoRefs.current).forEach((video) => {
        if (video && !video.paused) {
          video.pause();
        }
      });
      return;
    }

    // Pause all videos first
    Object.values(videoRefs.current).forEach((video) => {
      if (video && !video.paused) {
        video.pause();
      }
    });

    const currentSlideData = slides[currentSlide];

    if (currentSlideData?.type === "VIDEO") {
      const videoElement = videoRefs.current[currentSlide];

      if (videoElement) {
        // Ensure video is ready to play
        const playVideo = async () => {
          try {
            videoElement.currentTime = 0; // Reset to beginning
            await videoElement.play();
            console.log("Video started playing:", currentSlideData.url);
          } catch (error) {
            console.error("Error playing video:", error);
            // If video can't play, advance to next slide after a short delay
            setTimeout(() => {
              setCurrentSlide((prev) => (prev + 1) % slides.length);
            }, 1000);
          }
        };

        if (videoElement.readyState >= 3) {
          // HAVE_FUTURE_DATA
          playVideo();
        } else {
          videoElement.addEventListener("canplay", playVideo, { once: true });
        }
      }
    }
  }, [currentSlide, slides, state.isScreensaverActive]);

  // Auto-advance slides with video support
  useEffect(() => {
    if (!slides || slides.length <= 1) return;

    // Pause slider when screensaver is active
    if (state.isScreensaverActive) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Pause all videos
      Object.values(videoRefs.current).forEach((video) => {
        if (video && !video.paused) {
          video.pause();
        }
      });
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const slideDelay = state.appSettings?.sliderSlideDelay || 5000; // Default 5 seconds
    // console.log("Slider slide delay:", slideDelay, "ms");
    // console.log("App settings:", state.appSettings);

    const scheduleNextSlide = () => {
      const currentSlideData = slides[currentSlide];

      if (currentSlideData?.type === "VIDEO") {
        // For videos, wait for the video to end
        const videoElement = videoRefs.current[currentSlide];

        if (videoElement) {
          // Remove any existing event listeners
          videoElement.removeEventListener("ended", handleVideoEnd);

          // Add new event listener
          videoElement.addEventListener("ended", handleVideoEnd);

          // Calculate fallback timeout based on video duration or use default
          const fallbackTimeout = videoElement.duration
            ? Math.min(videoElement.duration * 1000 + 2000, 60000) // duration + 2s, max 60s
            : 30000; // 30 seconds fallback if duration unknown

          timeoutRef.current = setTimeout(() => {
            console.log("Video fallback timeout triggered, advancing slide");
            setCurrentSlide((prev) => (prev + 1) % slides.length);
            videoElement.removeEventListener("ended", handleVideoEnd);
          }, fallbackTimeout);
        } else {
          // If video element not found, use default delay
          timeoutRef.current = setTimeout(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
          }, slideDelay);
        }
      } else {
        // For images, use configured delay
        timeoutRef.current = setTimeout(() => {
          setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, slideDelay);
      }
    };

    // Start the first slide transition
    scheduleNextSlide();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [
    slides,
    currentSlide,
    state.appSettings?.sliderSlideDelay,
    state.isScreensaverActive,
    handleVideoEnd,
  ]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const goToPrevious = useCallback(() => {
    if (!slides) return;
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides]);

  const goToNext = useCallback(() => {
    if (!slides) return;
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides]);

  // Memoize slides rendering to prevent unnecessary re-renders
  const renderedSlides = useMemo(() => {
    if (!slides) return null;

    return slides.map((slide, index) => {
      // console.log("Rendering slider slide:", slide);
      // console.log("Slider slide URL:", slide.url);
      const isActive = index === currentSlide;
      return (
        <div
          key={slide.id}
          className={`${styles.slide} ${
            isActive ? styles.slideActive : styles.slideInactive
          }`}
        >
          {slide.type === "IMAGE" ? (
            <img
              className={styles.slideImage}
              src={slide.url}
              alt={slide.title || ""}
              onError={(e) => {
                console.log("Slider image load error for URL:", slide.url);
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
              }}
              onLoad={() => {
                console.log("Slider image loaded successfully:", slide.url);
              }}
            />
          ) : (
            <video
              key={videoKeys[index] || `video-${index}`}
              ref={(el) => {
                videoRefs.current[index] = el;
              }}
              className={styles.slideVideo}
              src={slide.url}
              autoPlay
              muted
              loop={false}
              playsInline
              preload="metadata"
              data-slide-index={index}
              onError={(e) => {
                console.log("Slider video load error for URL:", slide.url);
                const target = e.target as HTMLVideoElement;
                target.style.display = "none";
              }}
              onLoadStart={() => {
                console.log("Slider video load started:", slide.url);
              }}
              onLoadedMetadata={(e) => {
                console.log("Slider video metadata loaded:", slide.url);
                const video = e.target as HTMLVideoElement;
                console.log("Video duration:", video.duration, "seconds");
              }}
              onCanPlay={() => {
                console.log("Slider video can play:", slide.url);
              }}
              onPlay={() => {
                console.log("Slider video started playing:", slide.url);
              }}
              onPause={() => {
                console.log("Slider video paused:", slide.url);
              }}
            />
          )}
          {slide.title && (
            <div className={styles.slideTitle}>{slide.title}</div>
          )}
        </div>
      );
    });
  }, [slides, currentSlide, videoKeys]);

  if (isLoading) {
    return (
      <div className={styles.sliderContainer}>
        <div className={styles.loadingSpinner}>
          {t("loading", state.language)}...
        </div>
      </div>
    );
  }

  if (error || !slides || slides.length === 0) {
    return (
      <div className={styles.sliderContainer}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#666",
            fontSize: "18px",
          }}
        >
          {t("error", state.language)}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.sliderContainer}>
      {renderedSlides}

      {slides && slides.length > 1 && (
        <>
          <button
            className={`${styles.navigation} ${styles.prevButton}`}
            onClick={goToPrevious}
          >
            ‹
          </button>
          <button
            className={`${styles.navigation} ${styles.nextButton}`}
            onClick={goToNext}
          >
            ›
          </button>

          <div className={styles.dots}>
            {slides.map((_, index) => (
              <button
                key={index}
                className={`${styles.dot} ${
                  index === currentSlide ? styles.dotActive : ""
                }`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
